// Pull, subscribe, debounce, flush, retry, compact. The only module in the
// project that both talks to the network and knows what a lesson is.
//
// It never mutates the store's state incrementally: every time anything
// arrives it re-folds fold(base, seen ∪ tail) from the snapshot base, rather
// than applying the new event onto whatever `state` already holds. That is
// NOT the same guarantee as order-equivalence with a from-scratch fold.
// Only *discarding* a snapshot and re-folding from
// defaultState() over the full history always recovers the true order. Once
// `base` is a compacted, non-default snapshot, folding a late-arriving event
// onto it (a straggler whose hybrid-logical stamp precedes something already
// baked into `base`, but whose server position comes after the cursor)
// is contained in the result but may be ordered after events it
// logically precedes. That is accepted, because the local snapshot is a
// cache: only the straggler's effect must be present, not the order a full
// re-fold would give.
// A duplicate id is unaffected by any of this: fold()'s own dedup (fold.ts)
// makes a duplicate a true non-event regardless of which base it lands on.

import type { ProgressEvent } from "@aybuben/core/progress/event.ts";
import { fold } from "@aybuben/core/progress/fold.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { defaultState } from "@aybuben/core/storage.ts";

import type { ProgressStore } from "../progress/store.ts";
import { PAGE, REMOTE_VERSION, RemoteError, type RemoteProgress } from "./remote.ts";

/** A named constant and not a setting, until there is a measurement that justifies tuning it. */
const COMPACT_AFTER = 200;
const DEBOUNCE_MS = 2000;
const BACKOFF_CAP_MS = 60_000;

export interface Engine {
	start(): Promise<void>;
	stop(): void;
	flush(): Promise<void>;
}

export interface EngineDeps {
	store: ProgressStore;
	remote: RemoteProgress;
	debounceMs?: number;
	compactAfter?: number;
	/** Delay before retry attempt n (0-based). Injectable so tests need no real waiting. */
	backoff?: (attempt: number) => number;
	/** Window listeners, injectable so the engine is testable with no DOM. */
	listen?: (event: string, fn: () => void) => () => void;
	/** Called when the server rejects the session. The engine only stops; ending
	 * the session is the app's decision, and this is how it hears about it. */
	onUnauthorized?: () => void;
	/** The whole remote log has been folded in: account state is as fresh as it gets. */
	onPulled?: () => void;
}

const defaultBackoff = (attempt: number): number => {
	const base = Math.min(BACKOFF_CAP_MS, 1000 * 2 ** attempt);
	// Jitter, so a thousand tabs coming back online do not retry in lockstep.
	return base / 2 + Math.random() * (base / 2);
};

const defaultListen = (event: string, fn: () => void): (() => void) => {
	globalThis.addEventListener?.(event, fn);
	return () => globalThis.removeEventListener?.(event, fn);
};

export function createEngine(d: EngineDeps): Engine {
	const { store, remote } = d;
	const debounceMs = d.debounceMs ?? DEBOUNCE_MS;
	const compactAfter = d.compactAfter ?? COMPACT_AFTER;
	const backoff = d.backoff ?? defaultBackoff;
	const listen = d.listen ?? defaultListen;

	// The base the fold starts from, and everything folded on top of it that
	// did not come from this device's un-pushed tail.
	let base: GameState = defaultState();
	let baseCursor: string | null = null;
	let seen: ProgressEvent[] = [];

	let running = false;
	// Until a pull has folded the whole log in, a retry pulls before it pushes.
	let pulled = false;
	// The pull in flight: a debounced flush and an `online` that both arrive before it ends join it.
	let pulling: Promise<void> | null = null;
	let subscribed = false;
	let attempt = 0;
	let timer: ReturnType<typeof setTimeout> | null = null;
	let unsubs: (() => void)[] = [];

	const userId = () => store.getState().userId;

	const clearTimer = () => {
		if (timer !== null) {
			clearTimeout(timer);
			timer = null;
		}
	};

	const refold = (cursor: string | null) => {
		const tail = store.getState().tail;
		store.getState().adopt(fold(base, [...seen, ...tail]), tail, cursor);
	};

	async function pullAll(): Promise<number> {
		let cursor = baseCursor;
		let count = 0;
		for (;;) {
			const page = await remote.pullEvents(userId(), cursor, PAGE);
			for (const e of page.events) {
				// A hybrid logical clock only stays logical if it observes what it
				// pulls; without this a device with a slow clock keeps minting stamps
				// that sort before everything it has already seen.
				store.getState().ctx.clock.observe(e.at);
			}
			seen = [...seen, ...page.events];
			count += page.events.length;
			cursor = page.cursor;
			if (!page.more) break;
		}
		refold(cursor);
		return count;
	}

	async function flush(): Promise<void> {
		const s = store.getState();
		// A build that has met a future remote version refuses to fold and
		// refuses to push. It keeps working locally; it does not sync.
		if (s.sync === "stale" || !running) return;
		const tail = s.tail;
		if (!tail.length) {
			store.getState().setSync("idle");
			return;
		}
		store.getState().setSync("pushing");
		try {
			for (const e of tail) await remote.push(userId(), e);
			// Pushed events leave the tail but stay in the fold, so `seen` takes
			// them. A later pull returns them again and fold() dedupes by id.
			seen = [...seen, ...tail];
			store.getState().drain(
				tail.map((e) => e.id),
				store.getState().cursor,
			);
			store.getState().setSync("idle");
			attempt = 0;
		} catch (e) {
			// Never a modal, never a blocked UI. A failed sync is not a failed
			// lesson — the event is already durable in localStorage.
			store.getState().setSync("dirty");
			retryUnlessUnauthorized(e);
		}
	}

	/**
	 * A dead session will not fix itself on a timer, and retrying it forever is
	 * what manufactures rate-limit errors. The tail stays durable and dirty; a
	 * later record, an `online` event or the next launch tries once more, each
	 * driven by the learner rather than by a timer.
	 */
	function retryUnlessUnauthorized(e: unknown): void {
		if (e instanceof RemoteError && e.kind === "unauthorized") {
			// Also drops a debounced flush already queued, which would be one more
			// request against the same dead session.
			clearTimer();
			d.onUnauthorized?.();
			return;
		}
		scheduleRetry();
	}

	function scheduleRetry(): void {
		// A pull that fails after stop() schedules nothing.
		if (!running) return;
		clearTimer();
		const wait = backoff(attempt++);
		timer = setTimeout(() => {
			void sync();
		}, wait);
	}

	function scheduleFlush(): void {
		clearTimer();
		timer = setTimeout(() => {
			void sync();
		}, debounceMs);
	}

	async function start(): Promise<void> {
		running = true;
		await sync();
	}

	/** Pulls the whole log once (a launch offline pulls on the first retry that reaches the server), then pushes. */
	function sync(): Promise<void> {
		if (!running || store.getState().sync === "stale") return Promise.resolve();
		if (pulled) return flush();
		pulling ??= pullThenFlush().finally(() => (pulling = null));
		return pulling;
	}

	async function pullThenFlush(): Promise<void> {
		let snap = null;
		try {
			snap = await remote.pullSnapshot(userId());
		} catch (e) {
			store.getState().setSync("dirty");
			retryUnlessUnauthorized(e);
			subscribe();
			return;
		}

		if (snap && snap.version !== REMOTE_VERSION) {
			// Overwriting a future version with a past one destroys progress that
			// exists nowhere else. Refuse to fold, refuse to push, and say so.
			store.getState().setSync("stale");
			subscribe();
			return;
		}

		// A snapshot folded under other content is not wrong, it is
		// answering a different question. Discard it and re-fold from zero, which
		// is free because every snapshot is a cache. Narrowed into its own variable, not a boolean flag,
		// because a flag loses `snap`'s null-check for tsc across the ternaries
		// below.
		const usable = snap && snap.content === store.getState().content ? snap : null;
		base = usable ? usable.state : defaultState();
		baseCursor = usable ? usable.coversUpTo : null;
		seen = [];

		let arrived = 0;
		try {
			arrived = await pullAll();
		} catch (e) {
			if (e instanceof RemoteError && e.kind === "cursor") {
				// The watermark names a row the server does not have. Events are
				// never deleted, so this should be impossible — but "should be" is
				// not a guarantee, and a full re-fold is always correct.
				base = defaultState();
				baseCursor = null;
				seen = [];
				try {
					arrived = await pullAll();
				} catch (again) {
					store.getState().setSync("dirty");
					retryUnlessUnauthorized(again);
					subscribe();
					return;
				}
			} else {
				store.getState().setSync("dirty");
				retryUnlessUnauthorized(e);
				subscribe();
				return;
			}
		}

		if (!running) return;
		pulled = true;
		d.onPulled?.();

		// The one moment at which a snapshot's coverage is provably exact: every
		// event in the fold arrived through a pull, and the cursor is the last of
		// them. After a push it is not — see this file's header.
		if (arrived >= compactAfter && !store.getState().tail.length) {
			const s = store.getState();
			try {
				await remote.putSnapshot(s.userId, {
					version: REMOTE_VERSION,
					content: s.content,
					coversUpTo: s.cursor,
					state: s.state,
				});
				base = s.state;
				baseCursor = s.cursor;
				seen = [];
			} catch {
				// Purely a read optimization. Events are never deleted, so a snapshot
				// that fails to write costs one longer pull next launch and nothing else.
			}
		}

		subscribe();
		await flush();
	}

	function subscribe(): void {
		if (subscribed || !running) return;
		subscribed = true;
		let lastTail = store.getState().tail;
		unsubs.push(
			store.subscribe((s) => {
				if (s.tail === lastTail) return;
				lastTail = s.tail;
				if (s.tail.length) scheduleFlush();
			}),
		);
		// A flush here is an optimization, not durability: record() already
		// persisted synchronously. Do NOT reach for sendBeacon to "make this
		// reliable" — there is nothing to make reliable.
		unsubs.push(
			listen("pagehide", () => {
				void flush();
			}),
		);
		unsubs.push(
			listen("visibilitychange", () => {
				// No DOM lib here and `document` exists only in a browser; the cast is safe because only visibilityState is read, behind `?.`.
				const doc = (globalThis as { document?: { visibilityState?: string } }).document;
				if (doc?.visibilityState === "hidden") void flush();
			}),
		);
		unsubs.push(
			listen("online", () => {
				attempt = 0;
				void sync();
			}),
		);
	}

	function stop(): void {
		running = false;
		clearTimer();
		for (const off of unsubs) off();
		unsubs = [];
		subscribed = false;
	}

	return { start, stop, flush };
}
